import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Text, Extender } from "@amzn/storm-ui";
import { isEmpty } from "lodash";
import { useState, useRef, useEffect } from "react";
import { isNonNullish } from "remeda";

import {
    TELEMETRY_ID,
    PHOENIX_FEATURE,
    PHOENIX_TELEMETRY_ID,
} from "../../../../constants/telemetry";
import type { CardDetail, CardDetailsSource } from "../../../../types/card";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import { logWithTakt } from "../../../../utils/telemetry.utils";
import { LabelledTextField } from "../../../common/LabelledTextField";
import SkeletonLoader from "../../../common/SkeletonLoader";
import { DivFlexAlignCenter } from "../../../common/styled.components";

import { StyledCardDetailsText } from "./styles";

const MAXIMUM_EXPANDABLE_LINE_CLAMP = 20;

interface CardDetailsProps {
    details: CardDetail[];
    source: CardDetailsSource;
}

const CardDetailItem: React.FunctionComponent<{
    detail: CardDetail;
}> = ({ detail }) => {
    const {
        label,
        value,
        lineClamp,
        tertiaryText,
        expandable,
        additionalElements,
        showTooltip = true,
        logExpandClick = false,
        contentId,
        contentType,
    } = detail;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current && lineClamp && value) {
            const styledText = containerRef.current
                .firstElementChild as HTMLElement;
            if (styledText) {
                setIsTruncated(
                    styledText.scrollHeight > styledText.clientHeight,
                );
            }
        }
    }, [lineClamp, value]);

    const shouldRenderExtender = expandable && isTruncated;

    return (
        <div>
            <DivFlexAlignCenter ref={containerRef}>
                <StyledCardDetailsText
                    data-testid={`${label.toLowerCase().replace(/ /g, "-")}-details`}
                    title={showTooltip ? value : undefined}
                    lineClamp={
                        isExpanded ? MAXIMUM_EXPANDABLE_LINE_CLAMP : lineClamp
                    }
                >
                    <LabelledTextField label={label} value={value ?? ""} />
                </StyledCardDetailsText>
                {tertiaryText && (
                    <Text
                        data-testid={`${label.toLowerCase().replace(/ /g, "-")}-tertiary-text`}
                        color="tertiary"
                    >
                        {tertiaryText}
                    </Text>
                )}
                {additionalElements?.map(
                    (element) => element.visible && <>{element.render}</>,
                )}
            </DivFlexAlignCenter>
            {shouldRenderExtender && (
                <Extender
                    label={isExpanded ? "Hide" : "Show more"}
                    handleClick={() => {
                        if (logExpandClick) {
                            const action = isExpanded ? "hide" : "show";
                            logWithTakt(
                                TELEMETRY_ID.AI_SUMMARY_SHOW_MORE_CLICK,
                                {
                                    action,
                                    contentId,
                                    contentType,
                                },
                            );
                            if (action === "show") {
                                PhoenixMetrics.emit(
                                    PHOENIX_TELEMETRY_ID.AI_SUMMARY_SHOW_MORE_CLICK,
                                    PHOENIX_FEATURE.AI,
                                    {
                                        contentInfo:
                                            getPhoenixContentInfoFromStore(
                                                contentType as InsightType,
                                                contentId,
                                            ),
                                    },
                                );
                            } else {
                                PhoenixMetrics.emit(
                                    PHOENIX_TELEMETRY_ID.AI_SUMMARY_SHOW_MORE_CLICK_HIDE,
                                    PHOENIX_FEATURE.AI,
                                    {
                                        contentInfo:
                                            getPhoenixContentInfoFromStore(
                                                contentType as InsightType,
                                                contentId,
                                            ),
                                    },
                                );
                            }
                        }
                        setIsExpanded(!isExpanded);
                    }}
                    isOpen={isExpanded}
                />
            )}
        </div>
    );
};

export const CardDetails: React.FunctionComponent<CardDetailsProps> = (
    props: CardDetailsProps,
) => {
    const { details, source } = props;
    return (
        <>
            {details
                .filter((det) => {
                    if (isEmpty(det.value)) {
                        return false;
                    }
                    if (isNonNullish(det.renderOn)) {
                        return det.renderOn.includes(source);
                    }
                    return true;
                })
                .map((detail) => {
                    if (detail.loading) {
                        return <SkeletonLoader key={detail.label} />;
                    }
                    return (
                        <CardDetailItem key={detail.label} detail={detail} />
                    );
                })}
        </>
    );
};
